import AltRouteIcon from '@mui/icons-material/AltRoute';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import RouteIcon from '@mui/icons-material/RouteOutlined';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GroupsIcon from '@mui/icons-material/Groups';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { dec, formatMoney, formatNumber, moneyToString, type Locale } from '@fdt/shared';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
// The `size` API, which is the one that survives into MUI 7; the legacy `Grid`
// in v6 still wants `item xs={...}`.
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { reports, type Range } from '../api/reports';
import { DailyChart } from '../components/DailyChart';
import { EmptyState } from '../components/EmptyState';
import { IconTile } from '../components/IconTile';
import { InitialsAvatar } from '../components/InitialsAvatar';
import { MoneyText } from '../components/MoneyText';
import { RangePicker, presetRange } from '../components/RangePicker';
import { StatCard } from '../components/StatCard';
import { currentLocale } from '../i18n';

/**
 * The office's first screen.
 *
 * Six figures, a chart and two short lists — not everything that could be
 * counted. What is here is what someone opening the panel in the morning acts
 * on: what was sold, what came in, what is still owed, and whether the rounds
 * went out. Everything else is a report, one click away, where it can be sorted
 * and exported.
 *
 * The receivables card is deliberately the odd one out: it ignores the window,
 * because what a customer owes is a present fact and not a property of March.
 * Its tooltip says so, since a figure that does not follow the date picker
 * beside it would otherwise look like a bug.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [range, setRange] = useState<Range>(() => presetRange('last30'));

  const query = useQuery({
    queryKey: ['dashboard', range.from, range.to],
    queryFn: () => reports.dashboard(range),
    // Long enough not to re-run while someone reads it, short enough that a
    // document posted from a van shows up without a reload.
    staleTime: 60_000,
  });

  const data = query.data;
  const loading = query.isPending;

  const averageDocument =
    data && data.counts.documents > 0
      ? moneyToString(dec(data.totals.net).dividedBy(data.counts.documents))
      : '0.00';

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { lg: 'center' } }}
      >
        <Typography variant="h1">{t('nav.dashboard')}</Typography>
        <RangePicker value={range} onChange={setRange} />
      </Stack>

      {query.isError && <Alert severity="error">{t('errors.network')}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.net')}
            help={t('reports.dashboard.netHelp')}
            icon={TrendingUpIcon}
            tone="success"
            value={<MoneyText value={data?.totals.net ?? '0.00'} variant="h2" />}
            caption={
              data
                ? `${t('reports.dashboard.salesNet')} ${formatMoneyShort(data.totals.salesNet, locale)} · ${t('reports.dashboard.returnsNet')} ${formatMoneyShort(data.totals.returnsNet, locale)}`
                : undefined
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.collected')}
            help={t('reports.dashboard.collectedHelp')}
            icon={PaymentsIcon}
            tone="info"
            value={<MoneyText value={data?.collected ?? '0.00'} variant="h2" />}
            caption={
              data ? t('reports.dashboard.paymentCount', { count: data.counts.payments }) : undefined
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.documents')}
            icon={ReceiptLongIcon}
            tone="neutral"
            value={data ? formatNumber(data.counts.documents, locale, 0) : '0'}
            caption={
              data
                ? [
                    `${t('reports.dashboard.averageDocument')} ${formatMoneyShort(averageDocument, locale)}`,
                    data.counts.cancelledDocuments > 0
                      ? t('reports.dashboard.cancelledDocuments', {
                          count: data.counts.cancelledDocuments,
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : undefined
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.receivables')}
            help={t('reports.dashboard.receivablesHelp')}
            icon={WarningAmberIcon}
            tone="warning"
            value={<MoneyText value={data?.receivables.total ?? '0.00'} variant="h2" />}
            caption={
              data
                ? `${t('reports.dashboard.debtors', { count: data.receivables.debtorCount })} · ${t('reports.dashboard.overdue')} ${formatMoneyShort(data.receivables.overdueTotal, locale)}`
                : undefined
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.customersServed')}
            icon={GroupsIcon}
            tone="info"
            value={data ? formatNumber(data.counts.customersServed, locale, 0) : '0'}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.routes')}
            icon={AltRouteIcon}
            tone="accent"
            value={data ? formatNumber(data.routes.total, locale, 0) : '0'}
            caption={
              data
                ? t('reports.dashboard.routesCompleted', { count: data.routes.completed })
                : undefined
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.stopsVisited')}
            icon={PlaceIcon}
            tone="accent"
            // The one figure on this screen with a real denominator, so it is
            // the one that gets a bar. Guarded: a day with no stops planned
            // is the ordinary state of this card before the rounds go out.
            progress={
              data && data.routes.stopsPlanned > 0
                ? data.routes.stopsVisited / data.routes.stopsPlanned
                : undefined
            }
            value={
              data
                ? `${formatNumber(data.routes.stopsVisited, locale, 0)} / ${formatNumber(data.routes.stopsPlanned, locale, 0)}`
                : '0'
            }
            caption={
              data && data.routes.stopsSkipped > 0
                ? t('reports.dashboard.stopsSkipped', { count: data.routes.stopsSkipped })
                : undefined
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            loading={loading}
            label={t('reports.dashboard.plannedDistance')}
            icon={RouteIcon}
            tone="neutral"
            value={
              data ? `${formatNumber(data.routes.plannedDistanceMeters / 1000, locale, 1)} km` : '—'
            }
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
            <IconTile icon={ShowChartIcon} tone="success" size={36} />
            <Typography variant="h2">{t('reports.dashboard.perDay')}</Typography>
          </Stack>
          {loading ? (
            <Skeleton variant="rounded" height={240} />
          ) : data && data.days.some((day) => Number.parseFloat(day.salesNet) !== 0) ? (
            <DailyChart days={data.days} />
          ) : (
            <EmptyState icon={ShowChartIcon} title={t('reports.dashboard.noActivity')} />
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                <IconTile icon={ShoppingBagIcon} tone="info" size={34} />
                <Typography variant="h3">{t('reports.dashboard.topProducts')}</Typography>
              </Stack>
              <TopList
                loading={loading}
                empty={t('reports.dashboard.noActivity')}
                items={(data?.topProducts ?? []).map((product, index) => ({
                  key: product.productId,
                  // A rank rather than an icon: five identical box icons say
                  // nothing, while the position in the list is the fact the
                  // card is about.
                  leading: <Rank position={index + 1} />,
                  primary: product.name,
                  secondary: `${product.sku} · ${formatNumber(Number.parseFloat(product.quantity), locale, 2)}`,
                  value: product.net,
                }))}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                <IconTile icon={StorefrontIcon} tone="accent" size={34} />
                <Typography variant="h3">{t('reports.dashboard.topCustomers')}</Typography>
              </Stack>
              <TopList
                loading={loading}
                empty={t('reports.dashboard.noActivity')}
                items={(data?.topCustomers ?? []).map((customer) => ({
                  key: customer.customerId,
                  leading: (
                    <InitialsAvatar
                      name={customer.businessName}
                      colorKey={customer.customerId}
                      size={32}
                    />
                  ),
                  primary: customer.businessName,
                  secondary: `${customer.code} · ${t('reports.dashboard.collected')} ${formatMoneyShort(customer.collected, locale)}`,
                  value: customer.net,
                  to: `/customers/${customer.customerId}/account`,
                }))}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}

interface TopItem {
  key: string;
  primary: string;
  secondary: string;
  value: string;
  to?: string;
  /** A disc for a customer, a rank for a product. See `TopList`. */
  leading?: ReactNode;
}

/**
 * The position of a row in a top-five list.
 *
 * First place is filled in the accent and the rest are outlined, because the
 * list is short enough that a gradient over five steps would be invented
 * precision — what matters is which one is first.
 */
function Rank({ position }: { position: number }) {
  const first = position === 1;
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        width: 26,
        height: 26,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: first ? theme.palette.secondary.contrastText : 'text.secondary',
        backgroundColor: first ? theme.palette.secondary.main : 'transparent',
        border: first ? 'none' : `1px solid ${theme.palette.divider}`,
      })}
    >
      {position}
    </Box>
  );
}

/** The five-row lists under the chart. A table would be heavier than the data. */
function TopList({
  items,
  loading,
  empty,
}: {
  items: TopItem[];
  loading?: boolean;
  empty: string;
}) {
  if (loading) {
    return (
      <Stack spacing={1}>
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} height={28} />
        ))}
      </Stack>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
        {empty}
      </Typography>
    );
  }

  return (
    <Stack divider={<Divider flexItem />}>
      {items.map((item) => (
        <Stack
          key={item.key}
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center', py: 1, minWidth: 0 }}
        >
          {item.leading}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              component={item.to ? RouterLink : 'div'}
              {...(item.to ? { to: item.to } : {})}
              sx={{
                fontWeight: 500,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': item.to ? { color: 'secondary.main' } : undefined,
              }}
            >
              {item.primary}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {item.secondary}
            </Typography>
          </Box>
          <MoneyText value={item.value} variant="body2" sx={{ fontWeight: 600 }} />
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * An amount in a caption.
 *
 * Full precision, not rounded to whole euros: "Sales €206" sitting directly
 * under "€205.56" reads as a figure that disagrees with itself rather than as
 * a deliberate abbreviation.
 */
function formatMoneyShort(value: string, locale: Locale): string {
  return formatMoney(value, locale);
}
